import {
  DestroyRef,
  Injectable,
  NgZone,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  firstValueFrom,
  interval,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  expand,
  filter,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { CallFacadeService } from 'src/app/shared/agent-desktop/telephony/services/call-facade.service';
import { CallStatus } from 'src/app/shared/agent-desktop/telephony/models/call-status.enum';
import { AgentStatusService } from 'src/core/services/agent-status/agent-status.service';
import { AgentHeartbeatService } from 'src/core/services/signalr/agent-heartbeat.service';
import { ToasterService } from 'src/core/services/toaster/toaster.service';
import { CrmContactService } from 'src/app/features/contact/services/crm-contact.service';
import { CampaignProgressDto } from 'src/core/models/campaign/campaign-progress.dto';
import { ZoneOutsideTicker } from '../../shared/zone-outside-ticker';
import { CampaignOutboundClientService } from './campaign-outbound-client.service';
import { CampaignSummaryDto } from '../models/campaign-summary.dto';
import { CampaignContactReservationDto } from '../models/campaign-contact-reservation.dto';
import { CampaignWrapUpSummary } from '../models/campaign-wrap-up-summary.model';
import { CampaignReservationResultDto } from '../models/campaign-reservation-result.dto';
import { CampaignReservationOutcome } from '../models/campaign-reservation-outcome.enum';
import { CampaignAssignedWrapUpCode } from '../models/campaign-assigned-wrap-up-code.model';
import { CampaignOutboundPhase } from '../models/campaign-outbound-phase.enum';

const BCW_TICK_INTERVAL_MS = 1000;
const CAMPAIGN_LIST_POLL_INTERVAL_MS = 15000;
const MAX_RESERVE_ATTEMPTS = 5;
const HTTP_STATUS_CONFLICT = 409;
const DEFAULT_BEFORE_CALL_WORK_SECONDS = 30;
const DEFAULT_AFTER_CALL_WORK_SECONDS = 60;
const OUTBOUND_DIRECTION = 'outbound';
const RESERVE_CONFLICT = Symbol('campaign-reserve-conflict');
const HEADER_CAMPAIGN_ID = 'X-CX-Campaign-Id';
const HEADER_CAMPAIGN_CONTACT_ID = 'X-CX-Campaign-Contact-Id';
const HEADER_CALL_DIRECTION = 'X-CX-Call-Direction';

@Injectable({ providedIn: 'root' })
export class CampaignOutboundService {
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly client = inject(CampaignOutboundClientService);
  private readonly facade = inject(CallFacadeService);
  private readonly agentStatusService = inject(AgentStatusService);
  private readonly crmContactService = inject(CrmContactService);
  private readonly heartbeat = inject(AgentHeartbeatService);
  private readonly toaster = inject(ToasterService);

  private readonly destroy$ = new Subject<void>();
  private readonly bcwTicker = new ZoneOutsideTicker(this.zone, {
    intervalMs: BCW_TICK_INTERVAL_MS,
    onTick: wholeSeconds => this.onBcwTick(wholeSeconds),
  });
  private campaignListPollSubscription: Subscription | null = null;
  /**
   * True once the dial reached a live/answered call (Active/Held) or its ACW. Distinguishes the two ways
   * a Dialing engagement rests: an answered call owned by the ACW-completion flow (leave alone) vs an
   * immediate dial-failure that never answered and stranded the agent in BCW (must reset to Available).
   */
  private sawEstablishedCall = false;

  private readonly campaignsState = signal<CampaignSummaryDto[]>([]);
  private readonly selectedCampaignIdState = signal<number | null>(null);
  private readonly searchTermState = signal('');
  private readonly reservationState = signal<CampaignContactReservationDto | null>(null);
  private readonly phaseState = signal<CampaignOutboundPhase>(CampaignOutboundPhase.Idle);
  private readonly bcwSecondsState = signal(DEFAULT_BEFORE_CALL_WORK_SECONDS);
  private readonly afterCallWorkSecondsState = signal(DEFAULT_AFTER_CALL_WORK_SECONDS);
  private readonly bcwRemainingSecondsState = signal(0);
  private readonly remainingContactsState = signal(0);
  private readonly totalContactsState = signal(0);
  private readonly activeAgentCountState = signal(0);
  private readonly lastAbandonReasonState = signal<string | null>(null);
  /**
   * True while callNow() awaits the beginCall() backend transition (Reserved -> Calling). Drives the
   * Call Now button's loading/disabled state so it cannot be double-clicked during the round-trip.
   */
  private readonly callingState = signal(false);
  private readonly campaignWrapUpCodesState = signal<CampaignAssignedWrapUpCode[]>([]);
  /**
   * Campaign id whose assigned wrap-up codes are loaded/loading. Guards syncCampaignWrapUpCodes against
   * redundant fetches when the reservation changes for non-campaign reasons (e.g. CRM enrichment).
   */
  private loadedWrapUpCodesCampaignId: number | null = null;

  readonly campaigns = this.campaignsState.asReadonly();
  readonly selectedCampaignId = this.selectedCampaignIdState.asReadonly();
  readonly searchTerm = this.searchTermState.asReadonly();
  readonly reservation = this.reservationState.asReadonly();
  readonly phase = this.phaseState.asReadonly();
  readonly bcwSeconds = this.bcwSecondsState.asReadonly();
  readonly afterCallWorkSeconds = this.afterCallWorkSecondsState.asReadonly();
  readonly bcwRemainingSeconds = this.bcwRemainingSecondsState.asReadonly();
  readonly remainingContacts = this.remainingContactsState.asReadonly();
  readonly totalContacts = this.totalContactsState.asReadonly();
  readonly activeAgentCount = this.activeAgentCountState.asReadonly();
  readonly lastAbandonReason = this.lastAbandonReasonState.asReadonly();
  readonly calling = this.callingState.asReadonly();
  readonly campaignWrapUpCodes = this.campaignWrapUpCodesState.asReadonly();

  readonly activeCampaign: Signal<CampaignSummaryDto | null> = computed(() => {
    const id = this.selectedCampaignIdState();
    return this.campaignsState().find(campaign => campaign.campaignId === id) ?? null;
  });

  readonly filteredCampaigns: Signal<CampaignSummaryDto[]> = computed(() => {
    const term = this.searchTermState().trim().toLowerCase();
    const list = term
      ? this.campaignsState().filter(campaign => campaign.name.toLowerCase().includes(term))
      : this.campaignsState();
    return [...list].sort((left, right) => this.compareEndDate(left.endDateUtc, right.endDateUtc));
  });

  readonly bcwProgress = computed(() => {
    const total = this.bcwSecondsState();
    if (total <= 0) {
      return 0;
    }
    return (total - this.bcwRemainingSecondsState()) / total;
  });

  /**
   * True while the engagement is a campaign outbound call — from Call Now (Dialing) through the live
   * call and into ACW. Keys off reservation + Dialing phase rather than SIP session direction: the
   * session is already gone during ACW, when the ACW ring/script still need the campaign gate. False
   * during BCW (no call yet) and with no reservation.
   */
  readonly isCampaignCall = computed(
    () =>
      this.reservationState() !== null &&
      this.phaseState() === CampaignOutboundPhase.Dialing,
  );

  constructor() {
    this.heartbeat.campaignProgressChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(progress => this.onRemoteProgress(progress));

    this.heartbeat.campaignPoolExhausted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(campaignId => this.onRemotePoolExhausted(campaignId));

    // Clears the campaign engagement once a placed call comes to rest (see reconcileEngagement).
    effect(() => this.reconcileEngagement());

    effect(() => this.syncCampaignWrapUpCodes());

    this.destroyRef.onDestroy(() => {
      this.bcwTicker.stop();
      this.stopCampaignListPolling();
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  private loadCampaigns(): void {
    this.client
      .getAvailableCampaigns()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: campaigns => this.applyCampaignList(campaigns),
        error: () => this.toaster.error('Campaign::Outbound:LoadCampaignsFailed'),
      });
  }

  private applyCampaignList(campaigns: CampaignSummaryDto[]): void {
    this.campaignsState.set(campaigns);
    if (campaigns.length === 0) {
      this.stopCampaignListPolling();
    }
  }

  startCampaignListPolling(): void {
    this.stopCampaignListPolling();
    this.loadCampaigns();

    this.campaignListPollSubscription = interval(CAMPAIGN_LIST_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.client.getAvailableCampaigns().pipe(catchError(() => EMPTY))),
        takeUntil(this.destroy$),
      )
      .subscribe(campaigns => this.applyCampaignList(campaigns));
  }

  stopCampaignListPolling(): void {
    this.campaignListPollSubscription?.unsubscribe();
    this.campaignListPollSubscription = null;
  }

  selectCampaign(campaignId: number | null): void {
    this.selectedCampaignIdState.set(campaignId);
  }

  search(term: string): void {
    this.searchTermState.set(term);
  }

  take(): void {
    const campaignId = this.selectedCampaignIdState();
    if (campaignId === null || this.phaseState() === CampaignOutboundPhase.Reserving) {
      return;
    }

    this.phaseState.set(CampaignOutboundPhase.Reserving);

    let attempts = 0;
    this.reserveAttempt(campaignId)
      .pipe(
        expand(result => {
          if (result === RESERVE_CONFLICT && attempts < MAX_RESERVE_ATTEMPTS) {
            attempts += 1;
            return this.reserveAttempt(campaignId);
          }
          return EMPTY;
        }),
        filter(result => result !== RESERVE_CONFLICT),
        take(1),
        defaultIfEmpty(RESERVE_CONFLICT),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: result => this.handleReservationResult(campaignId, result),
        error: () => this.handleReservationError(),
      });
  }

  async callNow(): Promise<void> {
    // Re-entry guard: the manual "Call Now" button and the BCW countdown-zero auto-trigger can fire in
    // the same tick. Only proceed from the Bcw phase and when no beginCall is already in flight, so the
    // Reserved -> Calling transition (and the outbound dial) happens exactly once.
    if (this.phaseState() !== CampaignOutboundPhase.Bcw || this.callingState()) {
      return;
    }

    const reservation = this.reservationState();
    if (!reservation || !reservation.defaultPhoneNumber) {
      return;
    }

    this.stopBcwTicker();

    // Keystone: transition the reservation Reserved -> Calling on the backend BEFORE dialing. This is the
    // only path that advances the contact to Calling; without it the ACW success seam no-ops and the
    // reservation-expiry sweep reclaims the contact, so we must not place the SIP call unless it succeeds.
    if (reservation.campaignContactId != null) {
      this.callingState.set(true);
      try {
        const started = await this.beginCall(reservation.campaignContactId);
        if (!started) {
          this.onBeginCallFailed();
          return;
        }
      } finally {
        this.callingState.set(false);
      }
    }

    this.phaseState.set(CampaignOutboundPhase.Dialing);
    void this.agentStatusService.handleBcwCompleted('call-started');
    // defaultPhoneNumber is already the E.164 dial value (e.g. "+905551112233"); the switch
    // normalizes and routes it itself. Pass it through unchanged — no prefix, country code or
    // "+" rewrite here. CallFacadeService.makeCall() only trims and strips cosmetic separators.
    await this.facade.makeCall(
      reservation.defaultPhoneNumber,
      this.buildCampaignHeaders(reservation),
    );
  }

  private async beginCall(campaignContactId: number): Promise<boolean> {
    try {
      const result = await firstValueFrom(
        this.client.beginCall(campaignContactId).pipe(takeUntil(this.destroy$)),
      );
      return result?.started === true;
    } catch {
      return false;
    }
  }

  private onBeginCallFailed(): void {
    void this.agentStatusService.handleBcwCompleted('abandon');
    this.leaveCurrentCampaignGroup();
    this.reservationState.set(null);
    this.phaseState.set(CampaignOutboundPhase.Idle);
    this.toaster.warn('Campaign::Outbound:BeginCallFailed');
  }

  abandon(reason: string): void {
    const reservation = this.reservationState();
    this.lastAbandonReasonState.set(reason);
    this.stopBcwTicker();
    this.phaseState.set(CampaignOutboundPhase.Abandoned);
    void this.agentStatusService.handleBcwCompleted('abandon');

    if (reservation?.campaignContactId != null) {
      this.client
        .releaseReservation(reservation.campaignContactId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.onAbandonCompleted(),
          error: () => this.onAbandonCompleted(),
        });
      return;
    }

    this.onAbandonCompleted();
  }

  private startBcwTicker(): void {
    this.bcwTicker.start();
  }

  private stopBcwTicker(): void {
    this.bcwTicker.stop();
  }

  private buildCampaignHeaders(reservation: CampaignContactReservationDto): string[] {
    const headers = [`${HEADER_CALL_DIRECTION}: ${OUTBOUND_DIRECTION}`];

    const campaignId = reservation.campaignId ?? this.selectedCampaignIdState();
    if (campaignId != null) {
      headers.push(`${HEADER_CAMPAIGN_ID}: ${campaignId}`);
    }

    if (reservation.campaignContactId != null) {
      headers.push(`${HEADER_CAMPAIGN_CONTACT_ID}: ${reservation.campaignContactId}`);
    }

    return headers;
  }

  private reserveAttempt(
    campaignId: number,
  ): Observable<CampaignReservationResultDto | typeof RESERVE_CONFLICT> {
    return this.client.reserveNextContact(campaignId).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === HTTP_STATUS_CONFLICT) {
          return of(RESERVE_CONFLICT);
        }
        return throwError(() => error);
      }),
    );
  }

  private handleReservationResult(
    campaignId: number,
    result: CampaignReservationResultDto | typeof RESERVE_CONFLICT,
  ): void {
    if (result === RESERVE_CONFLICT) {
      this.onNoContactsAvailable();
      return;
    }

    switch (result.outcome) {
      case CampaignReservationOutcome.Reserved:
        this.onReserved(campaignId, result);
        break;
      case CampaignReservationOutcome.CampaignFinished:
        this.onCampaignFinished();
        break;
      case CampaignReservationOutcome.CampaignNotActive:
        this.onCampaignNotActive();
        break;
      case CampaignReservationOutcome.NoneAvailable:
      default:
        this.onNoContactsAvailable();
        break;
    }
  }

  private onReserved(campaignId: number, result: CampaignReservationResultDto): void {
    const bcwSeconds =
      result.beforeCallWorkSeconds > 0
        ? result.beforeCallWorkSeconds
        : DEFAULT_BEFORE_CALL_WORK_SECONDS;
    this.bcwSecondsState.set(bcwSeconds);
    this.afterCallWorkSecondsState.set(
      result.afterCallWorkSeconds > 0
        ? result.afterCallWorkSeconds
        : DEFAULT_AFTER_CALL_WORK_SECONDS,
    );
    this.reservationState.set(this.toReservation(result));
    this.phaseState.set(CampaignOutboundPhase.Bcw);
    this.bcwRemainingSecondsState.set(bcwSeconds);
    this.startBcwTicker();
    void this.agentStatusService.handleBcwStarted();
    this.enrichReservationFromCrm(result.defaultPhoneNumber);
    this.stopCampaignListPolling();
    this.heartbeat.joinCampaignGroup(campaignId);
  }

  private onCampaignFinished(): void {
    this.leaveCurrentCampaignGroup();
    this.phaseState.set(CampaignOutboundPhase.Exhausted);
    this.reservationState.set(null);
    this.selectedCampaignIdState.set(null);
    this.toaster.info('Campaign::Outbound:CampaignFinished');
  }

  private leaveCurrentCampaignGroup(): void {
    const campaignId = this.reservationState()?.campaignId ?? this.selectedCampaignIdState();
    if (campaignId != null) {
      this.heartbeat.leaveCampaignGroup(campaignId);
    }
  }

  private onCampaignNotActive(): void {
    this.phaseState.set(CampaignOutboundPhase.Idle);
    this.toaster.warn('Campaign::Outbound:CampaignNotActive');
  }

  private onNoContactsAvailable(): void {
    this.phaseState.set(CampaignOutboundPhase.Idle);
    this.toaster.info('Campaign::Outbound:NoContactsAvailable');
  }

  private handleReservationError(): void {
    this.phaseState.set(CampaignOutboundPhase.Idle);
    this.toaster.error('Campaign::Outbound:ReservationFailed');
  }

  private onAbandonCompleted(): void {
    this.leaveCurrentCampaignGroup();
    this.reservationState.set(null);
    this.phaseState.set(CampaignOutboundPhase.Idle);
  }

  /**
   * Ends a campaign engagement once its dial rests. Effect keyed off the Dialing phase plus the
   * telephony status/session/ACW signals. An answered call (Active/Held) or ACW marks it established;
   * a subsequent rest (Terminated/Failed/Registered/Idle, no live session, no ACW) resets it. An
   * immediate dial-failure that never established also forces the agent back to Available (otherwise
   * stranded in BCW). Trying/Ringing and the transient HangingUp/Reconnecting states are not "at rest".
   * callNow() sets Dialing and dials in the same synchronous block, so a rest here always means a
   * genuinely finished dial, never the pre-dial status.
   */
  private reconcileEngagement(): void {
    if (this.phaseState() !== CampaignOutboundPhase.Dialing) {
      this.sawEstablishedCall = false;
      return;
    }

    const status = this.facade.callStatus();
    const hasSession = this.facade.currentSession() !== null;
    const isAfterCallWork = this.facade.isAfterCallWork();

    if (isAfterCallWork || status === CallStatus.Active || status === CallStatus.Held) {
      this.sawEstablishedCall = true;
      return;
    }

    // Still connecting/ringing, a session object is briefly present, or a transient teardown
    // (HangingUp/Reconnecting): the dial is not finished — keep waiting.
    if (hasSession || !this.hasCallSettled(status)) {
      return;
    }

    if (this.sawEstablishedCall) {
      this.sawEstablishedCall = false;
      this.resetCampaignEngagement();
      return;
    }

    this.resetAfterDialFailure();
  }

  private hasCallSettled(status: CallStatus): boolean {
    return (
      status === CallStatus.Terminated ||
      status === CallStatus.Failed ||
      status === CallStatus.Registered ||
      status === CallStatus.Idle
    );
  }

  /**
   * Resets the engagement after an immediate dial-failure (never answered, no ACW). Returns the agent to
   * Available first — a never-answered dial leaves it parked in BCW, hiding the CAMPAIGNS panel — then
   * runs the common reset and notifies that the call could not be placed.
   */
  private resetAfterDialFailure(): void {
    void this.agentStatusService.handleBcwCompleted('abandon');
    this.resetCampaignEngagement();
    this.toaster.info('Campaign::Outbound:CallFailed');
  }

  private resetCampaignEngagement(): void {
    this.leaveCurrentCampaignGroup();
    this.reservationState.set(null);
    this.selectedCampaignIdState.set(null);
    this.phaseState.set(CampaignOutboundPhase.Idle);
    this.startCampaignListPolling();
  }

  private onBcwTick(wholeSeconds: number): void {
    const remaining = Math.max(0, this.bcwRemainingSecondsState() - wholeSeconds);
    this.bcwRemainingSecondsState.set(remaining);

    if (remaining === 0) {
      void this.callNow();
    }
  }

  private enrichReservationFromCrm(phoneNumber: string | null): void {
    if (!phoneNumber) {
      return;
    }

    this.crmContactService
      .getByPhoneNumber(phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: contact => {
          if (!contact) {
            return;
          }

          this.reservationState.update(current =>
            current
              ? {
                  ...current,
                  contactId: contact.id,
                  contactName: `${contact.firstName} ${contact.lastName}`.trim(),
                  crmContactNote: contact.note ?? null,
                }
              : current,
          );
        },
        error: () => undefined,
      });
  }

  private syncCampaignWrapUpCodes(): void {
    const campaignId = this.reservationState()?.campaignId ?? null;
    if (campaignId === this.loadedWrapUpCodesCampaignId) {
      return;
    }

    this.loadedWrapUpCodesCampaignId = campaignId;
    if (campaignId === null) {
      this.campaignWrapUpCodesState.set([]);
      return;
    }

    this.loadCampaignWrapUpCodes(campaignId);
  }

  private loadCampaignWrapUpCodes(campaignId: number): void {
    this.client
      .getAssignedWrapUpCodes(campaignId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: codes => {
          if (this.loadedWrapUpCodesCampaignId !== campaignId) {
            return;
          }
          this.campaignWrapUpCodesState.set(codes ?? []);
        },
        error: err => {
          if (this.loadedWrapUpCodesCampaignId === campaignId) {
            this.campaignWrapUpCodesState.set([]);
          }
          console.error('[CampaignOutbound] Failed to load campaign wrap-up codes:', err);
        },
      });
  }

  private onRemoteProgress(progress: CampaignProgressDto): void {
    this.applyProgressToCampaignRow(progress);

    if (progress.campaignId !== this.selectedCampaignIdState()) {
      return;
    }

    this.applyProgress(progress);
  }

  private applyProgressToCampaignRow(progress: CampaignProgressDto): void {
    this.campaignsState.update(list =>
      list.map(campaign =>
        campaign.campaignId === progress.campaignId
          ? {
              ...campaign,
              totalContacts: progress.total,
              completedContacts: Math.max(0, progress.total - progress.remaining),
              pendingEligibleCount: progress.remaining,
            }
          : campaign,
      ),
    );
  }

  private onRemotePoolExhausted(campaignId: number): void {
    if (campaignId !== this.selectedCampaignIdState()) {
      return;
    }

    this.onCampaignFinished();
  }

  private applyProgress(progress: CampaignProgressDto): void {
    this.totalContactsState.set(progress.total);
    this.remainingContactsState.set(progress.remaining);

    if (progress.activeCount != null) {
      this.activeAgentCountState.set(progress.activeCount);
    }
  }

  private toReservation(result: CampaignReservationResultDto): CampaignContactReservationDto {
    return {
      campaignContactId: result.campaignContactId,
      campaignId: result.campaignId,
      contactId: result.contactId,
      contactName: null,
      defaultPhoneNumber: result.defaultPhoneNumber,
      outboundCallerId: result.outboundCallerId,
      beforeCallWorkSeconds:
        result.beforeCallWorkSeconds > 0
          ? result.beforeCallWorkSeconds
          : DEFAULT_BEFORE_CALL_WORK_SECONDS,
      afterCallWorkSeconds:
        result.afterCallWorkSeconds > 0
          ? result.afterCallWorkSeconds
          : DEFAULT_AFTER_CALL_WORK_SECONDS,
      campaignCallCount: result.totalCallCount ?? 0,
      lastCallAt: result.lastAttemptTimeUtc ?? null,
      lastWrapUpCode: this.toWrapUpSummary(result.lastWrapUpName),
      lastConversationNote: result.lastConversationNote ?? null,
      crmContactNote: null,
      script: result.script ?? null,
      callWindows: result.callWindows ?? [],
      callWindowTimeZoneId: result.callWindowTimeZoneId || 'Etc/UTC',
    };
  }

  private toWrapUpSummary(name: string | null): CampaignWrapUpSummary | null {
    if (!name) {
      return null;
    }

    return { name };
  }

  private compareEndDate(left: string | null, right: string | null): number {
    if (left === right) {
      return 0;
    }
    if (left === null) {
      return 1;
    }
    if (right === null) {
      return -1;
    }
    return left < right ? -1 : 1;
  }
}
